'use client';

/**
 * VendorActions - Vendor-level quick actions
 *
 * Available actions:
 * - Browse catalog
 * - Request catalog update
 * - View order history
 * - Contact vendor
 * - View in Odoo
 *
 * @component_role Vendor-level Actions
 * @business_rule Vendor engagement actions: Request Catalog Update, Contact via
 *   WhatsApp, View Order History, Add to RFQ. "Open in Odoo" links to vendor
 *   partner form. Shows vendor contact info with one-click WhatsApp.
 * @user_outcome Reach out to vendors instantly - request updated pricing or check
 *   on delayed orders without leaving IPE.
 * @integration_point BFF data gateway → vendor_details strategy
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  Factory,
  RefreshCw,
  History,
  MessageSquare,
  ExternalLink,
  ChevronRight,
  Package,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '../details/shared';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface VendorActionsProps {
  vendorId: string;
}

export function VendorActions({ vendorId }: VendorActionsProps) {
  const detailStrategy = useCapabilityStore((s) => s.strategyName('vendor_intelligence', 'detail'));
  const { data } = useStrategyData(detailStrategy ?? 'vendor_details', { semantic_id: vendorId });
  const vendor = data?.data as Record<string, unknown> | undefined;

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  const actions = [
    {
      label: 'Browse Catalog',
      description: `${(vendor?.product_count as number) ?? 0} products available`,
      icon: Package,
      onClick: () => {
        toast.info('Browse Catalog', { description: 'Catalog browser coming soon' });
      },
      primary: true,
    },
    {
      label: 'Request Catalog Update',
      description: 'Ask vendor to refresh product data',
      icon: RefreshCw,
      onClick: () => {
        toast.info('Catalog Update', { description: 'Catalog sync request coming soon' });
      },
    },
    {
      label: 'View Order History',
      description: 'Past RFQs and purchase orders',
      icon: History,
      onClick: () => {
        toast.info('Order History', { description: 'History view coming soon' });
      },
    },
    {
      label: 'Contact Vendor',
      description: (vendor?.email as string) || (vendor?.phone as string) || 'Send a message',
      icon: MessageSquare,
      onClick: () => {
        toast.info('Contact', { description: 'Switching to Chat tab...' });
      },
    },
    {
      label: 'Open in Odoo',
      description: 'View full vendor details',
      icon: ExternalLink,
      onClick: () => {
        if (vendor?.odoo_id) {
          window.open(`${ODOO_HOST}/web#id=${vendor.odoo_id}&model=res.partner&view_type=form`, '_blank');
        } else {
          toast.error('Cannot open', { description: 'Vendor ID not available' });
        }
      },
      disabled: !vendor?.odoo_id,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b border-border bg-background/95 backdrop-blur-sm px-3 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Actions</span>
        </div>
        <Badge variant="secondary" className="text-[10px] px-1.5">
          {(vendor?.name as string) || vendorId}
        </Badge>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-thin">
        <div className="space-y-4 p-4">
          {/* Contact info */}
          {vendor && (vendor.email || vendor.phone) && (
            <div className="p-3 rounded-lg border border-border space-y-1">
              <div className="text-sm font-medium">{String(vendor.name)}</div>
              <div className="text-[10px] text-muted-foreground">
                {[vendor.email, vendor.phone].filter(Boolean).map(String).join(' · ')}
              </div>
            </div>
          )}

          {/* Actions */}
          <div>
            <SectionHeader title="QUICK ACTIONS" count={actions.length} />
            <div className="space-y-2">
              {actions.map((action, i) => (
                <button
                  key={i}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className={cn(
                    'w-full flex items-center justify-between p-2.5 rounded-lg border transition-colors text-left',
                    action.primary
                      ? 'bg-primary/5 border-primary/20 hover:bg-primary/10'
                      : 'border-border hover:bg-accent/50',
                    action.disabled && 'opacity-50 cursor-not-allowed',
                    !action.disabled && 'cursor-pointer',
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={cn(
                      'p-1 rounded-md shrink-0',
                      action.primary ? 'bg-primary/10' : 'bg-muted',
                    )}>
                      <action.icon className={cn(
                        'h-3.5 w-3.5',
                        action.primary ? 'text-primary' : 'text-muted-foreground',
                      )} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium">{action.label}</div>
                      <div className="text-[10px] text-muted-foreground truncate">{action.description}</div>
                    </div>
                  </div>
                  <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-[10px] text-muted-foreground/60 text-center pt-1 pb-2">
        {actions.length} actions available
      </div>
    </div>
  );
}
